import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { APPOINTMENT_STATUS_LABELS, type AppointmentStatus } from "@shared/modules/x-atelier";
import {
  SITE_REQUEST_STATUS_LABELS,
  type SiteRequest,
  type SiteRequestEvent,
  type SiteRequestStatus,
} from "@shared/modules/x-site";
import { api } from "../../../core/lib/api";
import { formatDateTime } from "../../../core/lib/format";
import { moduleEnabled } from "../../../core/blueprint";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input, Select } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { PageHeader } from "../../../core/components/ui/page-header";
import { useToastStore } from "../../../core/stores/toast";
// Exception assumée entre modules locaux (docs/MODULES.md) : la demande du
// site devient un rendez-vous dans la modal de l'atelier, la même partout.
import {
  AppointmentModal,
  type AppointmentPreset,
} from "../../x-atelier/components/appointment-modal";
import {
  deleteRequest,
  getRequestHistory,
  getRequestPrefill,
  listRequests,
  updateRequest,
  type RequestFilter,
  type RequestList,
} from "../lib/api";

const FILTERS: Array<{
  key: RequestFilter;
  label: string;
  count?: (c: RequestList["counts"]) => number;
}> = [
  { key: "actives", label: "Actives", count: (c) => c.nouveau + c.en_cours },
  { key: "a_traiter", label: "À traiter", count: (c) => c.nouveau },
  { key: "en_cours", label: "En cours", count: (c) => c.en_cours },
  { key: "traitees", label: "Traitées", count: (c) => c.traite },
  { key: "archivees", label: "Archivées", count: (c) => c.archive },
  { key: "toutes", label: "Toutes" },
];

const STATUS_VARIANT: Record<SiteRequestStatus, "warning" | "primary" | "success" | "neutral"> = {
  nouveau: "warning",
  en_cours: "primary",
  traite: "success",
  archive: "neutral",
};

/** Rendez-vous lié encore valable : un rendez-vous annulé libère la demande. */
function liveAppointment(r: SiteRequest): boolean {
  return Boolean(
    r.appointmentId &&
    r.appointmentStatus &&
    !["annule", "non_presente"].includes(r.appointmentStatus),
  );
}

/**
 * Demandes reçues du site : à traiter, en cours, traitées, archivées. Chaque
 * demande garde son responsable, ses notes et son historique ; « Créer un
 * rendez-vous » préremplit client, véhicule, services et préférence, ouvre les
 * créneaux disponibles et lie le rendez-vous à la demande, sans doublon —
 * aussi pour les anciennes demandes et les messages généraux.
 */
export function DemandesPage() {
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = (FILTERS.find((f) => f.key === searchParams.get("statut"))?.key ??
    "actives") as RequestFilter;
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  const [debounced, setDebounced] = useState(() => (searchParams.get("q") ?? "").trim());
  const [data, setData] = useState<RequestList | null>(null);
  const [failed, setFailed] = useState(false);
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [history, setHistory] = useState<Record<string, SiteRequestEvent[]>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<SiteRequest | null>(null);
  const [booking, setBooking] = useState<{
    request: SiteRequest;
    preset: AppointmentPreset;
  } | null>(null);
  const loadId = useRef(0);

  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if ((searchParams.get("q") ?? "") !== debounced) setParam("q", debounced || null);
    // L'URL suit la saisie : au retour, la liste est retrouvée telle quelle.
  }, [debounced]);

  const load = useCallback(() => {
    const id = ++loadId.current;
    listRequests(filter, debounced || undefined)
      .then((list) => {
        if (id !== loadId.current) return;
        setData(list);
        setFailed(false);
      })
      .catch(() => id === loadId.current && setFailed(true));
  }, [filter, debounced]);

  useEffect(load, [load]);

  useEffect(() => {
    api<Array<{ id: string; name: string }>>("/api/users")
      .then(setUsers)
      .catch(() => setUsers([]));
  }, []);

  const showHistory = async (id: string, force = false) => {
    if (history[id] && !force) {
      setHistory(({ [id]: _, ...rest }) => rest);
      return;
    }
    try {
      const events = await getRequestHistory(id);
      setHistory((h) => ({ ...h, [id]: events }));
    } catch {
      push("danger", "Historique indisponible");
    }
  };

  const update = async (
    r: SiteRequest,
    patch: { status?: SiteRequestStatus; assignedTo?: string | null; note?: string },
    done?: string,
  ) => {
    setBusy(r.id);
    try {
      await updateRequest(r.id, patch);
      if (done) push("success", done, r.name);
      if (patch.note) setNotes((n) => ({ ...n, [r.id]: "" }));
      load();
      if (history[r.id]) await showHistory(r.id, true);
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    setBusy(toDelete.id);
    try {
      await deleteRequest(toDelete.id);
      push("success", "Demande supprimée", toDelete.name);
      setToDelete(null);
      load();
    } catch {
      push("danger", "Suppression impossible");
    } finally {
      setBusy(null);
    }
  };

  /** Préremplit la modal de rendez-vous depuis la demande (anciennes comprises). */
  const startBooking = async (r: SiteRequest) => {
    setBusy(r.id);
    try {
      const p = await getRequestPrefill(r.id);
      const unmatched = p.unmatchedServices.length
        ? `Services demandés à retrouver au catalogue : ${p.unmatchedServices.join(", ")}.`
        : null;
      setBooking({
        request: r,
        preset: {
          client: p.client.id
            ? { id: p.client.id, name: p.client.name, phone: p.client.phone }
            : undefined,
          newClient: p.client.id
            ? undefined
            : { name: p.client.name, phone: p.client.phone, email: p.client.email },
          vehicleId: p.vehicle?.id ?? undefined,
          newVehicle:
            p.vehicle && !p.vehicle.id
              ? {
                  pricingClass: p.vehicle.pricingClass ?? "",
                  make: p.vehicle.make ?? "",
                  model: p.vehicle.model ?? "",
                  year: p.vehicle.year ? String(p.vehicle.year) : "",
                  plate: p.vehicle.plate ?? "",
                }
              : undefined,
          lines: p.lines.map((l) => ({ serviceId: l.serviceId, quantity: l.quantity })),
          notes: [p.notes, unmatched].filter(Boolean).join("\n"),
          source: "site_web",
          preference: p.preference,
          preferredDate: p.preferredDate,
          preferredPeriod: p.preferredPeriod,
          requestId: r.id,
          openSlots: true,
        },
      });
    } catch {
      push("danger", "Préparation du rendez-vous impossible");
    } finally {
      setBusy(null);
    }
  };

  const counts = data?.counts;
  const canBook = moduleEnabled("x-atelier");

  return (
    <div className="page demandes">
      <PageHeader
        title="Demandes du site"
        subtitle="Rendez-vous demandés sans créneau et messages reçus : à traiter, suivre, planifier et archiver."
        actions={
          <Button variant="ghost" icon={<Icon name="undo" size={16} />} onClick={load}>
            Actualiser
          </Button>
        }
      />

      <div className="chips chips--tabs" role="group" aria-label="Filtrer les demandes">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`chips__chip${filter === f.key ? " is-active" : ""}`}
            aria-pressed={filter === f.key}
            onClick={() => setParam("statut", f.key === "actives" ? null : f.key)}
          >
            {f.label}
            {f.count && counts ? <span className="demandes__count">{f.count(counts)}</span> : null}
          </button>
        ))}
      </div>

      <div className="demandes__toolbar">
        <Input
          type="search"
          aria-label="Rechercher une demande"
          placeholder="Nom, téléphone, courriel, véhicule, message…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {failed ? (
        <p className="empty">Demandes indisponibles — réessayez dans un instant.</p>
      ) : !data ? (
        <p className="empty">Chargement…</p>
      ) : data.requests.length === 0 ? (
        <p className="empty">
          {debounced
            ? `Aucune demande ne correspond à « ${debounced} ».`
            : "Aucune demande dans cette liste."}
        </p>
      ) : (
        <ul className="demandes__list">
          {data.requests.map((r) => {
            const linked = liveAppointment(r);
            return (
              <li key={r.id} className={`demande demande--${r.status}`}>
                <header className="demande__head">
                  <div className="demande__who">
                    <h2 className="demande__name">{r.name}</h2>
                    <p className="demande__meta">
                      {formatDateTime(r.createdAt, { dateStyle: "medium", timeStyle: "short" })}
                      {r.phone ? (
                        <>
                          {" · "}
                          <a href={`tel:${r.phone}`}>{r.phone}</a>
                        </>
                      ) : null}
                      {r.email ? (
                        <>
                          {" · "}
                          <a href={`mailto:${r.email}`}>{r.email}</a>
                        </>
                      ) : null}
                    </p>
                  </div>
                  <span className="demande__badges">
                    <Badge variant={r.kind === "reservation" ? "info" : "neutral"}>
                      {r.kind === "reservation" ? "Rendez-vous demandé" : "Message"}
                    </Badge>
                    <Badge variant={STATUS_VARIANT[r.status]}>
                      {SITE_REQUEST_STATUS_LABELS[r.status]}
                    </Badge>
                  </span>
                </header>

                <p className="demande__message">{r.message}</p>

                {r.appointmentId ? (
                  <p
                    className={`demande__appointment${linked ? "" : " demande__appointment--released"}`}
                  >
                    <Icon name="calendar" size={16} />
                    {linked ? "Rendez-vous " : "Rendez-vous annulé "}
                    {r.appointmentStartAt
                      ? formatDateTime(r.appointmentStartAt, {
                          dateStyle: "full",
                          timeStyle: "short",
                        })
                      : null}
                    {r.appointmentStatus
                      ? ` · ${APPOINTMENT_STATUS_LABELS[r.appointmentStatus as AppointmentStatus] ?? r.appointmentStatus}`
                      : null}{" "}
                    <Link to={`/travaux/${r.appointmentId}`}>Ouvrir</Link>
                  </p>
                ) : null}

                <div className="demande__actions">
                  {canBook ? (
                    linked ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Icon name="calendar" size={14} />}
                        onClick={() => navigate(`/travaux/${r.appointmentId}`)}
                      >
                        Voir le rendez-vous
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        icon={<Icon name="plus" size={14} />}
                        disabled={busy === r.id}
                        onClick={() => void startBooking(r)}
                      >
                        Créer un rendez-vous
                      </Button>
                    )
                  ) : null}
                  {r.status === "nouveau" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === r.id}
                      onClick={() => void update(r, { status: "en_cours" })}
                    >
                      Prendre en charge
                    </Button>
                  ) : null}
                  {r.status === "nouveau" || r.status === "en_cours" ? (
                    <Button
                      size="sm"
                      variant="success"
                      icon={<Icon name="check" size={14} />}
                      disabled={busy === r.id}
                      onClick={() => void update(r, { status: "traite" }, "Demande traitée")}
                    >
                      Marquer traitée
                    </Button>
                  ) : null}
                  {r.status === "archive" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === r.id}
                      onClick={() => void update(r, { status: "traite" }, "Demande désarchivée")}
                    >
                      Désarchiver
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === r.id}
                      onClick={() => void update(r, { status: "archive" }, "Demande archivée")}
                    >
                      Archiver
                    </Button>
                  )}
                  {r.clientId ? (
                    <Link className="demande__link" to={`/clients/${r.clientId}`}>
                      Fiche client
                      {r.clientName && r.clientName !== r.name ? ` (${r.clientName})` : ""}
                    </Link>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost-danger"
                    disabled={busy === r.id}
                    onClick={() => setToDelete(r)}
                  >
                    Supprimer
                  </Button>
                </div>

                <div className="demande__follow">
                  <Select
                    aria-label="Responsable"
                    value={r.assignedTo ?? ""}
                    disabled={busy === r.id}
                    onChange={(e) => void update(r, { assignedTo: e.target.value || null })}
                  >
                    <option value="">Aucun responsable</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                  <Input
                    aria-label="Note de suivi"
                    placeholder="Note de suivi (appel, courriel, devis envoyé…)"
                    value={notes[r.id] ?? ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && notes[r.id]?.trim()) {
                        e.preventDefault();
                        void update(r, { note: notes[r.id]!.trim() });
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!notes[r.id]?.trim() || busy === r.id}
                    onClick={() => void update(r, { note: notes[r.id]!.trim() })}
                  >
                    Ajouter la note
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-expanded={Boolean(history[r.id])}
                    onClick={() => void showHistory(r.id)}
                  >
                    Historique
                  </Button>
                </div>

                {history[r.id] ? (
                  <ol className="demande__history">
                    {history[r.id]!.map((h) => (
                      <li key={h.id}>
                        <span className="demande__history-when">
                          {formatDateTime(h.createdAt, { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                        <span>{h.description}</span>
                        {h.actorName ? (
                          <span className="demande__history-who">{h.actorName}</span>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={toDelete !== null}
        title="Supprimer cette demande ?"
        onClose={() => setToDelete(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setToDelete(null)}>
              Annuler
            </Button>
            <Button variant="danger" disabled={busy !== null} onClick={() => void remove()}>
              Supprimer définitivement
            </Button>
          </>
        }
      >
        <p>
          La demande de « {toDelete?.name} », ses notes et son historique seront supprimés. Un
          rendez-vous déjà créé depuis cette demande reste à l’agenda. Pour seulement dégager la
          liste, préférez « Archiver ».
        </p>
      </Modal>

      {booking ? (
        <AppointmentModal
          open
          preset={booking.preset}
          onClose={() => setBooking(null)}
          onSaved={(saved) => {
            setBooking(null);
            push("success", "Rendez-vous lié à la demande", saved.title);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
